import React from 'react';
import { User } from 'lucide-react';

const ProfilePictureFrame = ({ photoUrl, frameOption, size = 'w-48 h-48', className = '', borderColor = '#3b82f6', onClick }) => {
  const frameOptions = {
    lookingtowork: { text: 'LOOKINGTOWORK', color: '#22c55e', showBackground: true }, // Bright green with background
    exploringnewroles: { text: 'EXPLORINGNEWROLES', color: '#22c55e', showBackground: true }, // Bright green with background
    jobseeking: { text: 'JOBSEEKING', color: '#22c55e', showBackground: true }, // Bright green with background
    recruitingnow: { text: 'RECRUITINGNOW', color: '#1e3a8a', showBackground: true }, // Navy blue with background
    seekingtalent: { text: 'SEEKINGTALENT', color: '#1e3a8a', showBackground: true } // Navy blue with background
  };

  // Handle custom text frames (format: "custom:TEXT")
  let frame = frameOptions[frameOption];
  if (frameOption && frameOption.startsWith('custom:')) {
    const customText = frameOption.substring(7); // Remove "custom:" prefix
    frame = { text: customText, color: '#1e3a8a', showBackground: true }; // Blue with background
  }
  const sizeClasses = size.split(' ');
  const width = sizeClasses.find(c => c.startsWith('w-')) || 'w-48';
  const height = sizeClasses.find(c => c.startsWith('h-')) || 'h-48';
  
  // Calculate size in pixels (approximate based on Tailwind classes)
  const sizePx = width === 'w-48' ? 192 : width === 'w-32' ? 128 : width === 'w-24' ? 96 : 192;
  const center = sizePx / 2;
  const ringRadius = sizePx * 0.48; // 48% of image size - close to border
  const ringWidth = sizePx * 0.08; // 8% of image size
  const textRadius = ringRadius - ringWidth / 3; // Text sits closer to border (moved towards border)
  const fontSize = sizePx * 0.08; // 8% of image size
  
  // Generate unique IDs for SVG elements
  const textPathId = React.useMemo(() => `text-path-${width}-${Math.random().toString(36).substr(2, 9)}`, [width]);
  const bannerPathId = React.useMemo(() => `banner-path-${width}-${Math.random().toString(36).substr(2, 9)}`, [width]);

  const renderFrame = () => {
    if (!frame) return null;
    
    // Start at 280 degrees, end at 160 degrees (or lower if needed for longer text)
    // Shifted to the left by 110 degrees (90° + 20° more), then shifted lower, then 20° to the right, then 40° to the left (opposite direction, twice the distance)
    // Now moved down towards border, then shifted 15° counter-clockwise, then 10° more, then 15° more
    // Calculate end angle based on text length - longer text needs more arc
    const startAngle = 170; // Shifted 15° more counter-clockwise from 185° (185 - 15 = 170)
    // Estimate arc needed: each character needs about 9-10 degrees for proper spacing
    const anglePerChar = 9.5; // Increased slightly to ensure all characters fit
    const arcNeeded = frame.text.length * anglePerChar;
    let endAngle = startAngle - arcNeeded;
    // Ensure we don't go below 50 (160 - 110), but if text is long, allow lower
    const minEndAngle = 40; // Lowered to allow more space for longer text
    if (endAngle < minEndAngle) {
      endAngle = minEndAngle - (arcNeeded - (startAngle - minEndAngle)); // Extend below minEndAngle if needed
    }
    
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    // Calculate start and end points for text path
    const startX = center + textRadius * Math.cos(startAngleRad);
    const startY = center + textRadius * Math.sin(startAngleRad);
    const endX = center + textRadius * Math.cos(endAngleRad);
    const endY = center + textRadius * Math.sin(endAngleRad);
    
    // Large arc flag (1 for arc > 180 degrees)
    const largeArcFlag = Math.abs(startAngle - endAngle) > 180 ? 1 : 0;
    
    // Create the arc path for text
    const textArcPath = `M ${startX} ${startY} A ${textRadius} ${textRadius} 0 ${largeArcFlag} 0 ${endX} ${endY}`;
    
    // Create banner path - covers letters but shortened at the end, positioned on inside border of picture
    const bannerRadius = ringRadius - (ringWidth / 2); // Position at inner edge of white border circle
    const bannerStartAngle = startAngle; // Start exactly at text start (first letter)
    const bannerEndAngle = endAngle + (2 * anglePerChar); // End 2 letters before text end (removed part of end)
    const bannerStartAngleRad = (bannerStartAngle * Math.PI) / 180;
    const bannerEndAngleRad = (bannerEndAngle * Math.PI) / 180;
    
    const bannerStartX = center + bannerRadius * Math.cos(bannerStartAngleRad);
    const bannerStartY = center + bannerRadius * Math.sin(bannerStartAngleRad);
    const bannerEndX = center + bannerRadius * Math.cos(bannerEndAngleRad);
    const bannerEndY = center + bannerRadius * Math.sin(bannerEndAngleRad);
    
    // Create banner path - curved shape that follows the arc
    const bannerWidth = ringWidth * 1.2; // Banner is slightly wider than ring
    const bannerInnerRadius = bannerRadius - bannerWidth / 2;
    const bannerOuterRadius = bannerRadius + bannerWidth / 2;
    
    // Create banner as a curved band following the arc
    const bannerInnerStartX = center + bannerInnerRadius * Math.cos(bannerStartAngleRad);
    const bannerInnerStartY = center + bannerInnerRadius * Math.sin(bannerStartAngleRad);
    const bannerInnerEndX = center + bannerInnerRadius * Math.cos(bannerEndAngleRad);
    const bannerInnerEndY = center + bannerInnerRadius * Math.sin(bannerEndAngleRad);
    
    const bannerOuterStartX = center + bannerOuterRadius * Math.cos(bannerStartAngleRad);
    const bannerOuterStartY = center + bannerOuterRadius * Math.sin(bannerStartAngleRad);
    const bannerOuterEndX = center + bannerOuterRadius * Math.cos(bannerEndAngleRad);
    const bannerOuterEndY = center + bannerOuterRadius * Math.sin(bannerEndAngleRad);
    
    const bannerLargeArcFlag = Math.abs(bannerStartAngle - bannerEndAngle) > 180 ? 1 : 0;
    
    // Banner path: exactly matches text arc from start to end
    const bannerPath = `
      M ${bannerOuterStartX} ${bannerOuterStartY}
      A ${bannerOuterRadius} ${bannerOuterRadius} 0 ${bannerLargeArcFlag} 0 ${bannerOuterEndX} ${bannerOuterEndY}
      L ${bannerInnerEndX} ${bannerInnerEndY}
      A ${bannerInnerRadius} ${bannerInnerRadius} 0 ${bannerLargeArcFlag} 1 ${bannerInnerStartX} ${bannerInnerStartY}
      Z
    `;
    
    return (
      <div 
        className="absolute inset-0 z-20 rounded-full overflow-visible"
        style={{
          pointerEvents: 'none'
        }}
      >
        <svg
          className="absolute inset-0"
          width="100%"
          height="100%"
          viewBox={`0 0 ${sizePx} ${sizePx}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {/* Filter for text shadow */}
            <filter id={`shadow-${textPathId}`}>
              <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
              <feOffset dx="0" dy="2" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.5"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            
            {/* Path for text to follow along the ring */}
            <path
              id={textPathId}
              d={textArcPath}
              fill="none"
            />
          </defs>
          
          {/* Draw green background for specific frame options - only covers first letter to last letter */}
          {frame.showBackground && (
            <path
              d={bannerPath}
              fill={frame.color}
              opacity="0.95"
            />
          )}
          
          {/* Draw curved text along the ring path - full opacity */}
          <text
            fill="white"
            fontSize={fontSize}
            fontWeight="900"
            letterSpacing="0.1em"
            filter={`url(#shadow-${textPathId})`}
            style={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            <textPath
              href={`#${textPathId}`}
              startOffset="0%"
              method="align"
              spacing="auto"
              lengthAdjust="spacingAndGlyphs"
            >
              {frame.text}
            </textPath>
          </text>
        </svg>
      </div>
    );
  };

  return (
    <div className={`relative ${className} ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
      {photoUrl ? (
        <div className="relative">
          <img
            src={photoUrl}
            alt="Profile"
            className={`${width} ${height} rounded-full object-cover border-4 border-white relative z-10 ${onClick ? 'hover:opacity-90 transition-opacity' : ''}`}
            style={{ borderColor: 'white' }}
          />
          {renderFrame()}
        </div>
      ) : (
        <div className={`${width} ${height} rounded-full bg-gray-200 border-4 border-white relative z-10 flex items-center justify-center ${onClick ? 'hover:bg-gray-300 transition-colors' : ''}`}>
          <User className={`${width === 'w-48' ? 'w-20 h-20' : 'w-12 h-12'} text-gray-400`} />
          {renderFrame()}
        </div>
      )}
    </div>
  );
};

export default ProfilePictureFrame;
